import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { IValuesCollection } from './appTheme';

export enum nullHandlingBehaviour {
    ERROR_IF_NULLS = 0,
    OMIT_NULLS,
    WIDGET_DEFAULT_HANDLING,
    REPLACE_NULLS
}

export enum nullHandlingBackend {
    ERROR_IF_NULLS = 0,
    OMIT_NULLS,
    RETURN_NULLS
}

export enum useReplacingType {
    USE_DEFAULT = 0,
    USE_OWN,
    USE_STRING
}

export const REPLACE_NULL_STRING_DEFAULT = '<no value>';

export class NullHandling {
    private static defaultReplacingType = useReplacingType.USE_OWN;

    private behaviour = nullHandlingBehaviour.ERROR_IF_NULLS;
    private conversionValues: any = {};

    constructor(
        theme: IValuesCollection,
        ownTypeIsDefault?: boolean,
        defaultBehaviour = nullHandlingBehaviour.ERROR_IF_NULLS
    ) {
        let ownType = ownTypeIsDefault == null ? true : ownTypeIsDefault;

        this.behaviour = NullHandling.getThemeBehaviour(theme, defaultBehaviour);

        if (this.behaviour === nullHandlingBehaviour.REPLACE_NULLS) {
            let replace_forStrings = '&ltno value&gt';
            let replace_forNumeric = 0; // -1 ?
            let replace_forDates = new Date(0).toDateString();
            let replace_forBoolean = false;

            if (theme[themeVars.nullHandling_replaceForString] != null) {
                replace_forStrings = theme[themeVars.nullHandling_replaceForString];
            }

            if (theme[themeVars.nullHandling_replaceForNumeric] != null) {
                const rn = theme[themeVars.nullHandling_replaceForNumeric];

                replace_forNumeric = NullHandling.getReplacingValue_Numeric(rn, ownType);
            }

            if (theme[themeVars.nullHandling_replaceForBoolean] != null) {
                const rb = theme[themeVars.nullHandling_replaceForBoolean];

                replace_forBoolean = NullHandling.getReplacingValue_Boolean(rb, ownType);
            }

            if (theme[themeVars.nullHandling_replaceForDate] != null) {
                const rd = theme[themeVars.nullHandling_replaceForDate];

                replace_forDates = NullHandling.getReplacingValue_Date(rd, ownType);
            }

            this.conversionValues = {
                forString: replace_forStrings,
                forNumeric: replace_forNumeric,
                forDate: replace_forDates,
                forBoolean: replace_forBoolean
            };
        }
    }

    public getBehaviour() {
        return this.behaviour;
    }

    public static getThemeBehaviour(theme: IValuesCollection, defaultBehaviour = nullHandlingBehaviour.ERROR_IF_NULLS) {
        return theme[themeVars.nullHandling_behaviour] || defaultBehaviour;
    }

    public getBackendHandling() {
        switch (this.behaviour) {
            case nullHandlingBehaviour.ERROR_IF_NULLS:
                return nullHandlingBackend.ERROR_IF_NULLS;

            case nullHandlingBehaviour.OMIT_NULLS:
                return nullHandlingBackend.OMIT_NULLS;

            case nullHandlingBehaviour.REPLACE_NULLS:
            case nullHandlingBehaviour.WIDGET_DEFAULT_HANDLING:
                return nullHandlingBackend.RETURN_NULLS;
        }
    }

    public static getReplacingValue_Numeric(obj, defaultIsOwn?: boolean): any {
        // number or string
        const defOwn = defaultIsOwn == null ? true : defaultIsOwn;

        let value: any = 0;

        if (obj == null) {
            return value;
        }

        var replacingType = obj.replacingType == null ? NullHandling.defaultReplacingType : obj.replacingType;

        // - number:
        if (replacingType === useReplacingType.USE_OWN || (replacingType === useReplacingType.USE_DEFAULT && defOwn)) {
            if (obj.byOwnValue != null) {
                value = obj.byOwnValue;
            }
        }
        // - string:
        else if (
            replacingType === useReplacingType.USE_STRING ||
            (replacingType === useReplacingType.USE_DEFAULT && !defOwn)
        ) {
            value = '';

            if (obj.byStringValue != null) {
                value = obj.byStringValue;
            }
        }

        return value;
    }

    public static getReplacingValue_Boolean(obj, defaultIsOwn?: boolean): any {
        // boolean or string
        var defOwn = defaultIsOwn == null ? true : defaultIsOwn;

        var value: any = false;

        if (obj == null) {
            return value;
        }

        var replacingType = obj.replacingType == null ? NullHandling.defaultReplacingType : obj.replacingType;

        // - boolean:
        if (replacingType === useReplacingType.USE_OWN || (replacingType === useReplacingType.USE_DEFAULT && defOwn)) {
            if (obj.byOwnValue != null) {
                var v = obj.byOwnValue;
                if (typeof v === 'string') {
                    value = v === 'true';
                } else {
                    value = v;
                }
            }
        }
        // - string:
        else if (
            replacingType === useReplacingType.USE_STRING ||
            (replacingType === useReplacingType.USE_DEFAULT && !defOwn)
        ) {
            value = '';
            if (obj.byStringValue != null) {
                value = obj.byStringValue;
            }
        }

        return value;
    }

    public static getReplacingValue_Date(obj, defaultIsOwn?: boolean): any {
        // date or string
        var defOwn = defaultIsOwn == null ? true : defaultIsOwn;

        var value: any = new Date(0);
        if (obj == null) {
            return value;
        }

        var replacingType = obj.replacingType == null ? NullHandling.defaultReplacingType : obj.replacingType;

        // - date:
        if (replacingType === useReplacingType.USE_OWN || (replacingType === useReplacingType.USE_DEFAULT && defOwn)) {
            if (obj.byOwnValue != null) {
                value = new Date(obj.byOwnValue); // may be string "1970-01-23"  Must convert to date or leave string ?
            }
        }
        // - string:
        else if (
            replacingType === useReplacingType.USE_STRING ||
            (replacingType === useReplacingType.USE_DEFAULT && !defOwn)
        ) {
            value = '';
            if (obj.byStringValue != null) {
                value = obj.byStringValue;
            }
        }

        return value;
    }

    public handleNulls(value, dataType) {
        if (this.behaviour !== nullHandlingBehaviour.REPLACE_NULLS || value != null) {
            return value;
        }

        switch (dataType) {
            case 'date':
            case 'datetime':
            case 'time':
                return this.conversionValues.forDate;
            case 's':
            case 'string':
                return this.conversionValues.forString;
            case 'b':
            case 'boolean':
                return this.conversionValues.forBoolean;
            case 'i':
            case 'r':
            case 'number':
                return this.conversionValues.forNumeric;
            default:
                return value;
        }
    }
}
